import React from 'react'
import PropTypes from 'prop-types'
import { OverlayTrigger, Popover } from 'react-bootstrap'
import { FormattedMessage, useIntl } from 'react-intl'
import { DATA_KEYS, TONE } from '../../data/constants'
import { getDataGap, getFindingAffectedCount } from '../../data/reportModel'
import Chip from '../../components/Chip/Chip'
import EmptyState from '../../components/EmptyState/EmptyState'
import ReportTable from '../../components/ReportTable/ReportTable'
import {
  useAIRiskReportChannel,
  useAIRiskReportExportMode,
} from '../../components/RenderMode/AIRiskReportRenderModeProvider'
import { AI_RISK_REPORT_CHANNELS } from '../../data/reportChannel'
import SubArea from '../../components/SubArea/SubArea'
import SubBlock from '../../components/SubBlock/SubBlock'
import styles from './ThirdPartyAiSection.module.scss'

const HIGH_RISK_APP_ROLE_FINDING_ID = 'finding.app_with_high_risk_app_role'
const NEVER_ROTATING_SECRET_FINDING_ID = 'finding.never_rotating_secret'
const MULTI_TENANT_PERSONAL_AUDIENCE_FINDING_ID = 'finding.multi_tenant_personal_audience'
const APP_FINDING_CHIP = {
  [NEVER_ROTATING_SECRET_FINDING_ID]: {
    labelId: 'AI_RISK_REPORT.THIRD.APPS.ISSUE_NEVER_ROTATING_SECRET',
    tone: TONE.DANGER,
  },
  [MULTI_TENANT_PERSONAL_AUDIENCE_FINDING_ID]: {
    labelId: 'AI_RISK_REPORT.THIRD.APPS.ISSUE_MULTI_TENANT_PERSONAL_AUDIENCE',
    tone: TONE.WARN,
  },
}
const APP_FINDING_RANK = [MULTI_TENANT_PERSONAL_AUDIENCE_FINDING_ID, NEVER_ROTATING_SECRET_FINDING_ID]
const UNAVAILABLE_VALUE = '-'
const DATA_NOT_ASSESSED_MESSAGE_ID = 'AI_RISK_REPORT.DATA_NOT_ASSESSED'
const HIGH_RISK_APP_ROLES_POPOVER_TITLE_ID = 'AI_RISK_REPORT.THIRD.SP.HIGH_RISK_APP_ROLES_POPOVER_TITLE'
const HIGH_RISK_APP_ROLES_POPOVER_BODY_ID = 'AI_RISK_REPORT.THIRD.SP.HIGH_RISK_APP_ROLES_POPOVER_BODY'
const BETA_LONG_LIVED_SECRET_LABEL_ID = 'AI_RISK_REPORT.BETA.THIRD.APPS.ISSUE_LONG_LIVED_APP_SECRET'
const BETA_LONG_LIVED_SECRET_DEFINITION_ID = 'AI_RISK_REPORT.BETA.THIRD.APPS.LONG_LIVED_APP_SECRET_DEFINITION'
const AUDIENCE_LABEL_ID_BY_VALUE = {
  AzureADMyOrg: 'AI_RISK_REPORT.THIRD.APPS.AUDIENCE_SINGLE_TENANT',
  AzureADMultipleOrgs: 'AI_RISK_REPORT.THIRD.APPS.AUDIENCE_MULTI_TENANT',
  AzureADandPersonalMicrosoftAccount: 'AI_RISK_REPORT.THIRD.APPS.AUDIENCE_MULTI_PLUS_PERSONAL',
  PersonalMicrosoftAccount: 'AI_RISK_REPORT.THIRD.APPS.AUDIENCE_PERSONAL_ONLY',
}

const getPositiveCountTone = (hasValue, count, positiveTone = TONE.DANGER) => {
  if (!hasValue) return undefined
  return count > 0 ? positiveTone : TONE.OK
}

const getStatValue = (hasRows, count) => (hasRows || count > 0 ? count : UNAVAILABLE_VALUE)

const getTableFooter = ({ shown, total, messageId }) => {
  if (shown === 0 || total <= shown) return null

  return <FormattedMessage id={messageId} values={{ shown, total }} />
}

const getEmptyState = ({ gap, dataType, fallbackMessageId }) => {
  if (gap) return <EmptyState messageId={DATA_NOT_ASSESSED_MESSAGE_ID} values={{ dataType }} />
  return <EmptyState messageId={fallbackMessageId} tone={TONE.OK} />
}

const sortAppFindings = (findingIds = []) =>
  findingIds
    .filter((id) => APP_FINDING_RANK.includes(id))
    .slice()
    .sort((firstId, secondId) => APP_FINDING_RANK.indexOf(secondId) - APP_FINDING_RANK.indexOf(firstId))

const getAppPrimarySeverityRank = (findingIds = []) =>
  findingIds.reduce((max, id) => Math.max(max, APP_FINDING_RANK.indexOf(id)), -1)

const getAppFindingLabelId = (findingId, isBeta) => {
  if (isBeta && findingId === NEVER_ROTATING_SECRET_FINDING_ID) return BETA_LONG_LIVED_SECRET_LABEL_ID
  return APP_FINDING_CHIP[findingId]?.labelId
}

const renderAudience = (signInAudience) => {
  if (!signInAudience) return UNAVAILABLE_VALUE

  const labelId = AUDIENCE_LABEL_ID_BY_VALUE[signInAudience]
  if (!labelId) return signInAudience

  return <FormattedMessage id={labelId} />
}

const getAudienceSearchValues = (signInAudience, intl) => {
  const labelId = AUDIENCE_LABEL_ID_BY_VALUE[signInAudience]
  return [signInAudience, labelId ? intl.formatMessage({ id: labelId }) : null]
}

const formatPermissionCount = (count) => `${count} ${count === 1 ? 'permission' : 'permissions'}`

const getHighRiskApplicationPermissions = (row) =>
  (row.appRoleAssignmentSummary?.highRiskPermissions ?? []).filter((permission) => permission?.value)

const formatHighRiskPermission = ({ resourceName, value }) => (resourceName ? `${resourceName}: ${value}` : value)

const getHighRiskApplicationPermissionSearchValues = (row) =>
  getHighRiskApplicationPermissions(row).flatMap((permission) => [
    permission.resourceName,
    permission.value,
    formatHighRiskPermission(permission),
  ])

const renderHighRiskApplicationPermissionsPopover = (id, permissions, props) => (
  <Popover id={id} {...props} className={styles.permissionPopover}>
    <Popover.Header as="h3">
      <FormattedMessage id={HIGH_RISK_APP_ROLES_POPOVER_TITLE_ID} />
    </Popover.Header>
    <Popover.Body>
      <div className={styles.permissionPopoverBody}>
        <FormattedMessage id={HIGH_RISK_APP_ROLES_POPOVER_BODY_ID} />
      </div>
      <ul className={styles.permissionList}>
        {permissions.map((permission) => (
          <li key={`${permission.resourceId}:${permission.value}`}>
            <code>{formatHighRiskPermission(permission)}</code>
          </li>
        ))}
      </ul>
    </Popover.Body>
  </Popover>
)

const renderApplicationPermissions = (row, isExportMode) => {
  const totalCount = row.appRoleAssignmentSummary?.totalCount ?? 0
  const highRiskPermissions = getHighRiskApplicationPermissions(row)
  const label = formatPermissionCount(totalCount)

  if (highRiskPermissions.length === 0) return label

  if (isExportMode) {
    return (
      <div>
        <span className="text-danger fw-bold">{label}</span>
        <ul className={styles.permissionInlineList}>
          {highRiskPermissions.map((permission) => (
            <li key={`${permission.resourceId}:${permission.value}`}>
              <code>{formatHighRiskPermission(permission)}</code>
            </li>
          ))}
        </ul>
      </div>
    )
  }

  const popoverId = `ai-risk-high-risk-app-roles-${row.key}`

  return (
    <OverlayTrigger
      placement="top"
      trigger={['hover', 'focus']}
      overlay={(props) => renderHighRiskApplicationPermissionsPopover(popoverId, highRiskPermissions, props)}
    >
      <button type="button" className={`${styles.permissionValueButton} text-danger fw-bold`}>
        {label}
      </button>
    </OverlayTrigger>
  )
}

const renderLongLivedSecretFinding = ({ chip, isBeta, isExportMode, rowKey }) => {
  const labelId = getAppFindingLabelId(NEVER_ROTATING_SECRET_FINDING_ID, isBeta)
  const findingChip = (
    <Chip tone={chip.tone}>
      <FormattedMessage id={labelId} />
    </Chip>
  )

  if (!isBeta || isExportMode) return findingChip

  const popoverId = `ai-risk-long-lived-secret-${rowKey}`

  return (
    <OverlayTrigger
      placement="top"
      trigger={['hover', 'focus']}
      overlay={
        <Popover id={popoverId} className={styles.permissionPopover}>
          <Popover.Header as="h3">
            <FormattedMessage id={BETA_LONG_LIVED_SECRET_LABEL_ID} />
          </Popover.Header>
          <Popover.Body>
            <FormattedMessage id={BETA_LONG_LIVED_SECRET_DEFINITION_ID} />
          </Popover.Body>
        </Popover>
      }
    >
      <button type="button" className={`${styles.issuePopoverButton} d-inline-flex align-items-center gap-1`}>
        {findingChip}
      </button>
    </OverlayTrigger>
  )
}

const renderAppIssue = (findingIds = [], options = {}) => {
  const sorted = sortAppFindings(findingIds)
  if (sorted.length === 0) return UNAVAILABLE_VALUE

  return (
    <div className="d-flex flex-wrap gap-2">
      {sorted.map((id) => {
        const chip = APP_FINDING_CHIP[id]
        if (!chip) return null

        if (id === NEVER_ROTATING_SECRET_FINDING_ID) {
          return <React.Fragment key={id}>{renderLongLivedSecretFinding({ chip, ...options })}</React.Fragment>
        }

        return (
          <Chip key={id} tone={chip.tone}>
            <FormattedMessage id={chip.labelId} />
          </Chip>
        )
      })}
    </div>
  )
}

export default function AppsAndServicePrincipals({ summary, data, findings, score, gap = null, dataAvailability }) {
  const intl = useIntl()
  const isExportMode = useAIRiskReportExportMode()
  const channel = useAIRiskReportChannel()
  const isBeta = channel === AI_RISK_REPORT_CHANNELS.BETA
  const applications = data.applications ?? []
  const servicePrincipals = data.servicePrincipals ?? []
  const applicationRowsTotal = data.listTotals?.applications ?? applications.length
  const servicePrincipalRowsTotal = data.listTotals?.servicePrincipals ?? servicePrincipals.length
  const applicationGap = getDataGap(dataAvailability, [DATA_KEYS.APP_REGISTRATIONS])
  const servicePrincipalGap = getDataGap(dataAvailability, [DATA_KEYS.ENTERPRISE_APPS])
  const applicationRows = applications.map((app) => ({ ...app, key: app.entityId }))
  const servicePrincipalRows = servicePrincipals.map((sp) => ({ ...sp, key: sp.entityId }))
  const applicationTotal = summary.applications?.total ?? 0
  const servicePrincipalTotal = summary.servicePrincipals?.total ?? 0
  const hasApplications = applications.length > 0
  const hasServicePrincipals = servicePrincipals.length > 0
  const highRiskServicePrincipals = getFindingAffectedCount(findings, HIGH_RISK_APP_ROLE_FINDING_ID)
  const neverRotatingSecrets = getFindingAffectedCount(findings, NEVER_ROTATING_SECRET_FINDING_ID)
  const broadAudienceApps = getFindingAffectedCount(findings, MULTI_TENANT_PERSONAL_AUDIENCE_FINDING_ID)

  const stats = [
    {
      key: 'highRiskServicePrincipals',
      value: getStatValue(hasServicePrincipals, highRiskServicePrincipals),
      tone: getPositiveCountTone(hasServicePrincipals || highRiskServicePrincipals > 0, highRiskServicePrincipals),
      label: <FormattedMessage id="AI_RISK_REPORT.THIRD.APPS_AND_SPS.STAT_HIGH_RISK_SPS" />,
    },
    {
      key: 'neverRotatingSecrets',
      value: getStatValue(hasApplications, neverRotatingSecrets),
      tone: getPositiveCountTone(hasApplications || neverRotatingSecrets > 0, neverRotatingSecrets),
      label: (
        <FormattedMessage
          id={
            isBeta
              ? 'AI_RISK_REPORT.BETA.THIRD.APPS_AND_SPS.STAT_LONG_LIVED_APP_SECRET'
              : 'AI_RISK_REPORT.THIRD.APPS_AND_SPS.STAT_NEVER_ROTATING'
          }
        />
      ),
    },
    {
      key: 'broadAudienceApps',
      value: getStatValue(hasApplications, broadAudienceApps),
      tone: getPositiveCountTone(hasApplications || broadAudienceApps > 0, broadAudienceApps, TONE.WARN),
      label: <FormattedMessage id="AI_RISK_REPORT.THIRD.APPS_AND_SPS.STAT_BROAD_AUDIENCE" />,
    },
  ]

  const appColumns = [
    {
      key: 'app',
      header: <FormattedMessage id="AI_RISK_REPORT.THIRD.APPS.COL_APP" />,
      render: (row) => (
        <>
          <div className="text-body fw-bold">{row.displayName || row.appId}</div>
          <div className={styles.cellSub}>{row.appId}</div>
        </>
      ),
      sortable: true,
      sortValue: (row) => (row.displayName || row.appId || '').toLowerCase(),
      searchValue: (row) => [row.displayName, row.appId],
    },
    {
      key: 'audience',
      header: <FormattedMessage id="AI_RISK_REPORT.THIRD.APPS.COL_AUDIENCE" />,
      render: (row) => renderAudience(row.signInAudience),
      sortable: true,
      sortValue: (row) => row.signInAudience || '',
      searchValue: (row) => getAudienceSearchValues(row.signInAudience, intl),
    },
    {
      key: 'credentials',
      header: <FormattedMessage id="AI_RISK_REPORT.THIRD.APPS.COL_CREDENTIALS" />,
      render: (row) =>
        `${(row.credentialSummary?.passwordCredentialCount ?? 0) + (row.credentialSummary?.keyCredentialCount ?? 0)} secret`,
      sortable: true,
      sortValue: (row) =>
        (row.credentialSummary?.passwordCredentialCount ?? 0) + (row.credentialSummary?.keyCredentialCount ?? 0),
      searchValue: (row) => {
        const count =
          (row.credentialSummary?.passwordCredentialCount ?? 0) + (row.credentialSummary?.keyCredentialCount ?? 0)
        return [count, `${count} secret`]
      },
    },
    {
      key: 'issue',
      header: <FormattedMessage id="AI_RISK_REPORT.COL_ISSUE" />,
      render: (row) => renderAppIssue(row.findingIds || [], { isBeta, isExportMode, rowKey: row.key }),
      sortable: true,
      sortValue: (row) => getAppPrimarySeverityRank(row.findingIds || []),
      searchValue: (row) =>
        sortAppFindings(row.findingIds || []).map((id) => intl.formatMessage({ id: getAppFindingLabelId(id, isBeta) })),
    },
  ]

  const spColumns = [
    {
      key: 'app',
      header: <FormattedMessage id="AI_RISK_REPORT.THIRD.SP.COL_APP" />,
      render: (row) => (
        <>
          <div className="text-body fw-bold">{row.displayName || row.applicationId}</div>
          <div className={styles.cellSub}>{row.applicationId}</div>
        </>
      ),
      sortable: true,
      sortValue: (row) => (row.displayName || row.applicationId || '').toLowerCase(),
      searchValue: (row) => [row.displayName, row.applicationId],
    },
    {
      key: 'appRoles',
      header: <FormattedMessage id="AI_RISK_REPORT.THIRD.SP.COL_APP_ROLES" />,
      render: (row) => renderApplicationPermissions(row, isExportMode),
      sortable: true,
      sortValue: (row) => row.appRoleAssignmentSummary?.totalCount ?? 0,
      searchValue: (row) => {
        const count = row.appRoleAssignmentSummary?.totalCount ?? 0
        return [count, formatPermissionCount(count), ...getHighRiskApplicationPermissionSearchValues(row)]
      },
    },
    {
      key: 'delegated',
      header: <FormattedMessage id="AI_RISK_REPORT.THIRD.SP.COL_DELEGATED" />,
      render: (row) => formatPermissionCount(row.delegatedPermissionSummary?.totalCount ?? 0),
      sortable: true,
      sortValue: (row) => row.delegatedPermissionSummary?.totalCount ?? 0,
      searchValue: (row) => {
        const count = row.delegatedPermissionSummary?.totalCount ?? 0
        return [count, formatPermissionCount(count)]
      },
    },
    {
      key: 'issue',
      header: <FormattedMessage id="AI_RISK_REPORT.COL_ISSUE" />,
      render: (row) =>
        (row.findingIds || []).includes(HIGH_RISK_APP_ROLE_FINDING_ID) ? (
          <Chip tone={TONE.DANGER}>
            <FormattedMessage id="AI_RISK_REPORT.THIRD.SP.ISSUE_APP_WITH_HIGH_RISK_APP_ROLE" />
          </Chip>
        ) : (
          '-'
        ),
      sortable: true,
      sortValue: (row) => ((row.findingIds || []).includes(HIGH_RISK_APP_ROLE_FINDING_ID) ? 1 : 0),
      searchValue: (row) =>
        (row.findingIds || []).includes(HIGH_RISK_APP_ROLE_FINDING_ID)
          ? intl.formatMessage({ id: 'AI_RISK_REPORT.THIRD.SP.ISSUE_APP_WITH_HIGH_RISK_APP_ROLE' })
          : null,
    },
  ]

  return (
    <SubBlock
      titleId="AI_RISK_REPORT.THIRD.APPS_AND_SPS.TITLE"
      subtitleId="AI_RISK_REPORT.THIRD.APPS_AND_SPS.SUBTITLE"
      score={score.value}
      tone={score.tone}
      gap={gap}
      stats={stats}
    >
      <div className="d-grid gap-3">
        <SubArea titleId="AI_RISK_REPORT.THIRD.SP.TITLE">
          <ReportTable
            columns={spColumns}
            rows={servicePrincipalRows}
            totalRows={servicePrincipalRowsTotal}
            affectedEntityLabelId="AI_RISK_REPORT.TABLE.ENTITY.SERVICE_PRINCIPALS"
            footer={getTableFooter({
              shown: servicePrincipalRows.length,
              total: servicePrincipalTotal,
              messageId: 'AI_RISK_REPORT.THIRD.SP.TABLE_FOOTER',
            })}
            empty={getEmptyState({
              gap: servicePrincipalGap,
              dataType: 'Service principal',
              fallbackMessageId: 'AI_RISK_REPORT.NO_SERVICE_PRINCIPALS',
            })}
          />
        </SubArea>
        <SubArea titleId="AI_RISK_REPORT.THIRD.APPS.TITLE">
          <ReportTable
            columns={appColumns}
            rows={applicationRows}
            totalRows={applicationRowsTotal}
            affectedEntityLabelId="AI_RISK_REPORT.TABLE.ENTITY.APP_REGISTRATIONS"
            footer={getTableFooter({
              shown: applicationRows.length,
              total: applicationTotal,
              messageId: 'AI_RISK_REPORT.THIRD.APPS.TABLE_FOOTER',
            })}
            empty={getEmptyState({
              gap: applicationGap,
              dataType: 'App registrations',
              fallbackMessageId: 'AI_RISK_REPORT.NO_APP_REGISTRATIONS',
            })}
          />
        </SubArea>
      </div>
    </SubBlock>
  )
}

AppsAndServicePrincipals.propTypes = {
  summary: PropTypes.shape({
    applications: PropTypes.shape({ total: PropTypes.number }),
    servicePrincipals: PropTypes.shape({ total: PropTypes.number }),
  }).isRequired,
  data: PropTypes.shape({
    applications: PropTypes.arrayOf(PropTypes.shape({})),
    servicePrincipals: PropTypes.arrayOf(PropTypes.shape({})),
    listTotals: PropTypes.shape({
      applications: PropTypes.number,
      servicePrincipals: PropTypes.number,
    }),
  }).isRequired,
  findings: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  score: PropTypes.shape({ value: PropTypes.number, tone: PropTypes.string }).isRequired,
  gap: PropTypes.shape({ reason: PropTypes.string }),
  dataAvailability: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
}
